import { useOverlayTriggerState } from '@react-stately/overlays';
import type { ReactElement } from 'react';
import React, { useEffect, useRef } from 'react';
import type { DialogClose, DialogTriggerProps } from '../../../interface-and-type';
import { Modal } from '../../date-picker-overlays/modal';
import { Tray } from '../../date-picker-overlays/tray';
import { DialogTriggerBase } from './controls/dialog-trigger-base';
import { PopoverTrigger } from './controls/popover-trigger';

const DialogTrigger = (props: DialogTriggerProps) => {
  let {
    children,
    type = 'modal',
    mobileType = type === 'popover' ? 'modal' : type,
    hideArrow,
    targetRef,
    isDismissable,
    isKeyboardDismissDisabled,
    ...positionProps
  } = props;
  if (!Array.isArray(children) || children.length > 2) {
    throw new Error('DialogTrigger must have exactly 2 children');
  }

  let [trigger, content] = children as [ReactElement, DialogClose];

  let state = useOverlayTriggerState(props);
  let wasOpen = useRef(false);
  wasOpen.current = state.isOpen;
  let isExiting = useRef(false);
  let onExiting = () => (isExiting.current = true);
  let onExited = () => (isExiting.current = false);

  useEffect(() => {
    return () => {
      if ((wasOpen.current || isExiting.current) && type !== 'popover' && type !== 'tray') {
        console.warn(
          'A DialogTrigger unmounted while open. This is likely due to being placed within a trigger that unmounts or inside a conditional. Consider using a DialogContainer instead.'
        );
      }
    };
  }, []);

  if (type === 'popover') {
    return (
      <PopoverTrigger
        {...positionProps}
        state={state}
        targetRef={targetRef}
        trigger={trigger}
        content={content}
        isKeyboardDismissDisabled={isKeyboardDismissDisabled}
        hideArrow={hideArrow}
      />
    );
  }

  let renderOverlay = () => {
    switch (type) {
      case 'fullscreen':
      case 'fullscreenTakeover':
      case 'modal':
        return (
          <Modal
            isOpen={state.isOpen}
            isDismissable={type === 'modal' ? isDismissable : false}
            onClose={state.close}
            type={type}
            isKeyboardDismissDisabled={isKeyboardDismissDisabled}
            onExiting={onExiting}
            onExited={onExited}
          >
            {typeof content === 'function' ? content(state.close) : content}
          </Modal>
        );
      case 'tray':
        return (
          <Tray
            isOpen={state.isOpen}
            onClose={state.close}
            isKeyboardDismissDisabled={isKeyboardDismissDisabled}
          >
            {typeof content === 'function' ? content(state.close) : content}
          </Tray>
        );
    }
  };

  return (
    <DialogTriggerBase
      type={type}
      state={state}
      isDismissable={isDismissable}
      trigger={trigger}
      overlay={renderOverlay() as any}
    />
  );
};

DialogTrigger.getCollectionNode = function* (props: DialogTriggerProps) {
  let [trigger] = React.Children.toArray(props.children);
  let [, content] = props.children as [ReactElement, DialogClose];
  yield {
    element: trigger,
    wrapper: (element: any) => (
      <DialogTrigger key={element.key} {...props}>
        {element}
        {content}
      </DialogTrigger>
    )
  };
};

let _DialogTrigger = DialogTrigger as (props: DialogTriggerProps) => JSX.Element;
export { _DialogTrigger as DialogTrigger };
